import React from 'react';
import { Card, Statistic, Row, Col, Empty } from 'antd';
import { 
  ThunderboltOutlined, 
  BarChartOutlined, 
  CalendarOutlined,
  DashboardOutlined 
} from '@ant-design/icons';
import Chart from 'react-apexcharts';
import PropTypes from 'prop-types';

const ConsumoEnergiaWidget = ({ consumoEnergia }) => {
  if (!consumoEnergia || consumoEnergia.totalConsumo === 0) {
    return (
      <Card 
        title="Consumo de Energía" 
        className="dashboard-widget dashboard-widget--energia"
      >
        <Empty 
          description="No hay datos de consumo disponibles"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return Number(num).toLocaleString('es-ES', { maximumFractionDigits: 2 });
  };

  // Configuración del gráfico de consumo diario
  const chartOptions = {
    chart: {
      type: 'area',
      height: 200,
      toolbar: { show: false },
      sparkline: { enabled: false }
    },
    colors: ['#1890ff'],
    dataLabels: { enabled: false },
    stroke: {
      curve: 'smooth',
      width: 2
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.3,
        opacityTo: 0.1
      }
    },
    xaxis: {
      type: 'datetime',
      labels: {
        format: 'dd/MM'
      }
    },
    yaxis: {
      labels: {
        formatter: (val) => formatNumber(val)
      }
    },
    tooltip: {
      x: {
        format: 'dd/MM/yyyy'
      },
      y: {
        formatter: (val) => `${formatNumber(val)} kWh`
      }
    },
    grid: {
      show: true,
      borderColor: '#f0f0f0',
      strokeDashArray: 4
    }
  };

  const chartSeries = [{
    name: 'Consumo',
    data: consumoEnergia.consumoPorDia.map(item => ({
      x: new Date(item.fecha).getTime(),
      y: item.consumo
    }))
  }];

  // Configuración del gráfico de consumo por tipo (donut)
  const donutOptions = {
    chart: {
      type: 'donut',
      height: 200
    },
    labels: Object.keys(consumoEnergia.consumoPorTipo),
    colors: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1'],
    legend: {
      position: 'bottom',
      fontSize: '12px'
    },
    tooltip: {
      y: {
        formatter: (val) => `${formatNumber(val)} kWh`
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: () => `${formatNumber(consumoEnergia.totalConsumo)}`
            }
          }
        }
      }
    }
  };

  const donutSeries = Object.values(consumoEnergia.consumoPorTipo);

  return (
    <Card 
      title="Consumo de Energía" 
      className="dashboard-widget dashboard-widget--energia"
      extra={<ThunderboltOutlined />}
    >
      <Row gutter={[16, 16]}>
        {/* Estadísticas principales */}
        <Col span={24}>
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Statistic 
                title="Consumo Total" 
                value={consumoEnergia.totalConsumo}
                formatter={(value) => formatNumber(value)}
                suffix="kWh"
                prefix={<ThunderboltOutlined />}
              />
            </Col>
            <Col span={12}>
              <Statistic 
                title="Sensores Activos" 
                value={consumoEnergia.sensoresIncluidos}
                prefix={<DashboardOutlined />}
              />
            </Col>
          </Row>
        </Col>

        {/* Período */}
        {consumoEnergia.periodoCalculado && (
          <Col span={24}>
            <div className="energia-period">
              <CalendarOutlined />
              <span>{consumoEnergia.periodoCalculado.mes}</span>
            </div>
          </Col>
        )}

        {/* Gráfico de consumo diario */}
        {consumoEnergia.consumoPorDia && consumoEnergia.consumoPorDia.length > 0 && (
          <Col span={24}>
            <div className="energia-chart">
              <h4>
                <BarChartOutlined /> Consumo Diario
              </h4>
              <Chart
                options={chartOptions}
                series={chartSeries}
                type="area"
                height={200}
              />
            </div>
          </Col>
        )}

        {/* Gráfico de consumo por tipo */}
        {Object.keys(consumoEnergia.consumoPorTipo).length > 0 && (
          <Col span={24}>
            <div className="energia-chart">
              <h4>Consumo por Tipo</h4>
              <Chart
                options={donutOptions}
                series={donutSeries}
                type="donut"
                height={200}
              />
            </div>
          </Col>
        )}

        {/* Resumen por tipo */}
        <Col span={24}>
          <div className="energia-summary">
            <h4>Detalle por Tipo</h4>
            <div className="energia-types">
              {Object.entries(consumoEnergia.consumoPorTipo).map(([tipo, valor]) => (
                <div key={tipo} className="energia-type">
                  <span className="energia-type__name">{tipo}</span>
                  <span className="energia-type__value">
                    {formatNumber(valor)} kWh
                  </span>
                  <span className="energia-type__percentage">
                    ({((valor / consumoEnergia.totalConsumo) * 100).toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

ConsumoEnergiaWidget.propTypes = {
  consumoEnergia: PropTypes.shape({
    totalConsumo: PropTypes.number.isRequired,
    consumoPorTipo: PropTypes.object.isRequired,
    consumoPorDia: PropTypes.arrayOf(
      PropTypes.shape({
        fecha: PropTypes.string.isRequired,
        consumo: PropTypes.number.isRequired
      })
    ).isRequired,
    sensoresIncluidos: PropTypes.number.isRequired,
    periodoCalculado: PropTypes.shape({
      inicio: PropTypes.string.isRequired,
      fin: PropTypes.string.isRequired,
      mes: PropTypes.string.isRequired
    })
  })
};

ConsumoEnergiaWidget.defaultProps = {
  consumoEnergia: null
};

export default ConsumoEnergiaWidget;
